import { MapPin, MoreHorizontal } from "lucide-react";
import { useState } from "react";

import { CardActions, createCardActionsProps } from "@/src/components/cards/card-actions";
import LocationCardEditable from "@/src/components/cards/location-card/location-card-editable";
import { CopyableContent } from "@/src/components/copyable-content";
import { Button } from "@/src/components/shadcn/ui/button";
import { Card, CardTitle, CardHeader, CardContent } from "@/src/components/shadcn/ui/card";
import { ContextMenu, ContextMenuTrigger } from "@/src/components/shadcn/ui/context-menu";
import { DropdownMenu, DropdownMenuTrigger } from "@/src/components/shadcn/ui/dropdown-menu";
import { useUser } from "@/src/context/user-context";
import { canWriteSOR } from "@/src/lib/rbac";
import { formatUSPhoneNumber } from "@/src/lib/utils/date-and-phone-utils";
import { Location } from "@/src/models/location";

export default function LocationCard({
  location,
  type,
  isEditable = false,
  onEditLocation,
  onEditLocationLoading,
}: {
  location: Location;
  type: string;
  isEditable?: boolean;
  // Callback for when user switches to a different location - allows parent to handle the context update
  onEditLocation?: (updatedLocation: Location) => Promise<void>;
  onEditLocationLoading?: boolean;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const { roles } = useUser();
  const canWrite = canWriteSOR(roles);
  const effectiveIsEditable = isEditable && canWrite;

  if (!location) {
    return null;
  }

  const handleEditToggle = () => {
    setIsEditing(!isEditing);
  };

  if (isEditing) {
    return (
      <LocationCardEditable
        location={location}
        onClose={handleEditToggle}
        type={type}
        onEditLocation={onEditLocation}
        onEditLocationLoading={onEditLocationLoading}
      />
    );
  }

  const cardActionsProps = createCardActionsProps({
    id: location.id,
    label: "Location ID",
    href: `/locations/${location.displayId}`,
    customProps: [{ label: "Edit Location", onClick: handleEditToggle }],
  });

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        <Card className="w-full">
          <CardHeader>
            <div className="flex items-center">
              <MapPin className="h-6 w-6 mr-2" />
              <CardTitle className="text-md font-semibold">{`${type} Location`}</CardTitle>
              {effectiveIsEditable && (
                <div className="ml-auto flex items-center">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" className="flex h-8 w-8 p-0 data-[state=open]:bg-muted">
                        <MoreHorizontal className="h-4 w-4" />
                        <span className="sr-only">Open menu</span>
                      </Button>
                    </DropdownMenuTrigger>
                    <CardActions {...cardActionsProps} isContextMenu={false} />
                  </DropdownMenu>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div>
                <span className="font-semibold">Name:</span> <CopyableContent text={location.name || "N/A"} />
              </div>
              <div>
                <span className="font-semibold">EHR ID:</span> <CopyableContent text={location.ehrId || "N/A"} />
              </div>
              <div>
                <span className="font-semibold">Phone:</span>{" "}
                <CopyableContent text={formatUSPhoneNumber(location.phoneNumber) || "N/A"} />
              </div>
              <div>
                <span className="font-semibold">Fax:</span>{" "}
                <CopyableContent text={formatUSPhoneNumber(location.faxPhoneNumber) || "N/A"} />
              </div>
              {location.address && (
                <>
                  <div>
                    <span className="font-semibold">Address:</span>{" "}
                    <CopyableContent text={location.address.line1 || "N/A"} />
                  </div>
                  {location.address.line2 && (
                    <div>
                      <span className="font-semibold">Address 2:</span>{" "}
                      <CopyableContent text={location.address.line2 || "N/A"} />
                    </div>
                  )}
                  <div>
                    <span className="font-semibold">City:</span>{" "}
                    <CopyableContent text={location.address.city || "N/A"} />
                  </div>
                  <div>
                    <span className="font-semibold">State:</span>{" "}
                    <CopyableContent text={location.address.state || "N/A"} />
                  </div>
                  <div>
                    <span className="font-semibold">Zip:</span>{" "}
                    <CopyableContent text={location.address.postalCode || "N/A"} />
                  </div>
                </>
              )}
              {location.facilityType && (
                <div>
                  <span className="font-semibold">Facility Type:</span> <CopyableContent text={location.facilityType} />
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </ContextMenuTrigger>
      {effectiveIsEditable && <CardActions {...cardActionsProps} isContextMenu={true} />}
    </ContextMenu>
  );
}
